import { useState, useEffect } from "react";

import { useAuthState } from "@/zustand/AuthState";
import {
  sealDk,
  unsealDk,
  hasSealedDk,
  clearSealedDk,
} from "@/lib/crypto/sealedKey";
import { createLogger } from "@/utils/debug";

const log = createLogger("AuthSession");

/**
 * Derives the logged-in state AND restores the in-memory master key (`dk`).
 *
 * `dk` is no longer persisted to localStorage (see AuthState `partialize`); it is
 * sealed under a non-extractable key in IndexedDB. `username` + `tk` still
 * persist and rehydrate synchronously, so on reload we look logged-in before
 * `dk` exists. This hook is the single async gate that restores `dk` before the
 * protected app (which does crypto) mounts, keeping `loading` true meanwhile.
 *
 * Cases (session = `username` && `tk` both present):
 *  - no session             -> not logged in.
 *  - session, `dk` present  -> fresh login, or a legacy plaintext `dk` that just
 *                              rehydrated: seal it if not already sealed
 *                              (one-time, seamless migration), then logged in.
 *  - session, `dk` absent   -> reload: unseal into memory. If recovered, logged
 *                              in; if not (cleared/corrupt seal), the session is
 *                              unusable, so reset it and send the user to login.
 */
function useAuthSession() {
  const token = useAuthState((state) => state.tk);
  const username = useAuthState((state) => state.username);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      // No session -> nothing to unlock.
      if (!(username && token)) {
        if (!cancelled) {
          setIsLoggedIn(false);
          setLoading(false);
        }
        return;
      }

      const { dk, setDerivedKey, resetAuthState } = useAuthState.getState();

      try {
        if (dk) {
          // Fresh login or legacy plaintext dk still in memory: ensure the sealed
          // store matches the CURRENT in-memory dk. Re-seal when nothing is
          // sealed yet, OR when the sealed value differs from `dk` — e.g. after a
          // password change / KDF upgrade, or a new login (possibly a different
          // account) following a crash where logout's clearSealedDk never ran.
          // Sealing only "if not already sealed" would leave a stale key sealed
          // and restore the WRONG dk on the next reload. A corrupt/unreadable
          // seal counts as a mismatch and is overwritten rather than crashing.
          let sealedMatches = false;
          try {
            sealedMatches = (await hasSealedDk()) && (await unsealDk()) === dk;
          } catch {
            sealedMatches = false;
          }
          if (!sealedMatches) {
            await sealDk(dk);
          }
          // Purge any legacy plaintext `dk` that rehydrated from a pre-sealing
          // client's localStorage. `dk` is now safely sealed; re-setting it runs
          // the store's partialize (which excludes `dk`), rewriting the persisted
          // blob without the plaintext copy. `dk` stays in memory.
          setDerivedKey(dk);
          if (!cancelled) {
            setIsLoggedIn(true);
          }
        } else {
          // Reload: recover dk from the sealed store into memory.
          const recovered = await unsealDk();
          if (recovered) {
            setDerivedKey(recovered);
            if (!cancelled) {
              setIsLoggedIn(true);
            }
          } else {
            // Tokens say logged-in but the key can't be recovered -> the session
            // can't decrypt anything. Reset it so routing falls back to login.
            log.warn(
              "No recoverable dk for an active session; forcing re-login",
            );
            await clearSealedDk();
            resetAuthState();
            if (!cancelled) {
              setIsLoggedIn(false);
            }
          }
        }
      } catch (err) {
        log.warn("Auth session unlock failed", err);
        if (!cancelled) {
          setIsLoggedIn(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [username, token]);

  return { isLoggedIn, loading, username };
}

export default useAuthSession;
